import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { useAppStore } from '../../context/store';
import { useCasesByIds } from '../../hooks/useCases';
import { CaseOutline } from '../cases/CaseDrawing';

const THUMB_SCALE = 0.055;

// Floating tray with the cases picked for comparison; sticks to the bottom of the scroll area
export const CompareDock: React.FC = () => {
  const { comparison, removeFromComparison, clearComparison } = useAppStore();
  const { cases } = useCasesByIds(comparison.selectedCases);

  if (comparison.selectedCases.length === 0) {
    return null;
  }

  const selected = comparison.selectedCases
    .map((id) => cases.find((c) => c.id === id))
    .filter((c) => c !== undefined);

  return (
    <div className="sticky bottom-4 z-30 px-4 pointer-events-none">
      <div
        role="region"
        aria-label="Comparison"
        className="pointer-events-auto mx-auto flex max-w-3xl flex-wrap items-center gap-x-5 gap-y-3 rounded-2xl bg-fg py-3 pl-5 pr-3 text-canvas shadow-dock animate-slide-up"
      >
        <span className="font-mono text-xs uppercase tracking-label opacity-70">
          Compare {comparison.selectedCases.length}/4
        </span>

        <ul className="flex flex-1 flex-wrap gap-2">
          {selected.map((c) => (
            <li
              key={c.id}
              className="flex h-10 items-center gap-2 rounded-lg border border-canvas/25 pl-2.5 pr-1 text-sm font-medium"
            >
              {c.dimensions && (
                <span className="flex h-[26px] items-end">
                  <CaseOutline
                    width={Math.max(6, Math.round(c.dimensions.width * THUMB_SCALE))}
                    height={Math.round(c.dimensions.height * THUMB_SCALE)}
                    selected
                  />
                </span>
              )}
              <span className="hidden max-w-[120px] truncate sm:inline">{c.name}</span>
              <button
                type="button"
                onClick={() => removeFromComparison(c.id)}
                aria-label={`Remove ${c.manufacturer} ${c.name} from comparison`}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md opacity-70 hover:bg-canvas/10 hover:opacity-100"
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={clearComparison}
          className="h-11 px-3 text-sm opacity-75 hover:opacity-100"
        >
          Clear
        </button>
        <Link
          to="/compare"
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-accent-fg hover:bg-accent/90 transition-colors"
        >
          Compare cases
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};
